/**
 * The original's continuous engine sound, as a specification in code.
 *
 * Everything here is a measured constant or a pure function over one — no
 * `AudioContext`, no chip, no worklet globals — so the whole recipe is
 * unit-testable and the DSP layer (`engine-voice-dsp.ts`) and the worklet
 * (`engine-worklet.ts`) stay thin wrappers around it.
 *
 * The mechanism, from `docs/formats/sound-music.md` § "Continuous engine sound
 * (RE)" and `docs/engineering/audio-plan.md` §6 (live DOSBox-X OPL capture of
 * four flights, `re-sessions/engine-sound/`):
 *
 * > One OPL2 voice. Bank program 4 (`SIMSNDS` cue 2's patch), keyed on once
 * > when the flight starts and never keyed again, at a carrier fundamental the
 * > sim rewrites continuously between 24.37 Hz and 55.47 Hz — and *nothing
 * > else*: the level never changes, the voice is never retriggered, there is no
 * > second layer.
 *
 * So there is deliberately no throttle-driven gain, no tremolo layer, no
 * cylinder-firing modulator and no "engine catches" one-shot in this file. The
 * perceived firing rate rises with throttle for free: the modulator runs at
 * half the carrier (`MULT 0`) and therefore sweeps 12.2 → 27.7 Hz.
 */

/**
 * Sierra's 48-entry quarter-semitone F-number octave, `0x157`…`0x2A4`. The SCI
 * AdLib driver ships this as a table rather than a formula, and the live
 * capture recovered exactly these 48 values as the only F-numbers OPL channel 0
 * ever takes — so this is the ladder the sim moves along, not an approximation
 * of one. Identical to `packages/baker/src/adlib-driver.ts`'s `NOTE_FREQUENCIES`
 * (duplicated rather than imported: that module is the bake-side MIDI driver and
 * pulls in `@red-opus/formats`, which a worklet bundle must not).
 */
export const SIERRA_FNUM_TABLE: readonly number[] = [
  0x157, 0x15c, 0x161, 0x166, 0x16b, 0x171, 0x176, 0x17b, 0x181, 0x186, 0x18c, 0x192, 0x198, 0x19e,
  0x1a4, 0x1aa, 0x1b0, 0x1b6, 0x1bd, 0x1c3, 0x1ca, 0x1d0, 0x1d7, 0x1de, 0x1e5, 0x1ec, 0x1f3, 0x1fa,
  0x202, 0x209, 0x211, 0x218, 0x220, 0x228, 0x230, 0x238, 0x241, 0x249, 0x252, 0x25a, 0x263, 0x26c,
  0x275, 0x27e, 0x287, 0x290, 0x29a, 0x2a4,
];

/** Entries per octave in `SIERRA_FNUM_TABLE`; the pitch index is `48 × block + tableIndex`. */
export const FNUM_TABLE_SIZE = 48;

/** MEASURED: minimum throttle — F-number 514, block 0, carrier fundamental 24.37 Hz. */
export const ENGINE_INDEX_MIN = 28;
/** MEASURED: the throttle law's full travel, 57 quarter-semitone steps = 14.25 semitones (ratio 2.276). */
export const ENGINE_INDEX_SPAN = 57;
/** MEASURED: maximum throttle — F-number 585, block 1, carrier fundamental 55.47 Hz. */
export const ENGINE_INDEX_MAX = ENGINE_INDEX_MIN + ENGINE_INDEX_SPAN;

/** The OPL2 channel the sim's voice allocator always handed the engine (it is the first sound the sim starts). */
export const ENGINE_OPL_CHANNEL = 0;
/** The second voice, for the nearest other aircraft's distance-modelled engine (see `ENEMY_LEVEL_LADDER`). */
export const ENEMY_OPL_CHANNEL = 1;

/**
 * MEASURED: `index = 28 + floor(57 × throttle)` fits all thirteen notches of a
 * clean 12-press idle→ceiling ladder exactly (`rb_009.dro`) — 28, 32, 37, 42,
 * 47, 51, 56, 61, 66, 70, 75, 80, 85. Two aircraft (Sopwith Camel, SPAD 7) hit
 * the same floor and ceiling, so this is a global law, not a per-aircraft table.
 *
 * The pitch is therefore *exponential* in throttle, not proportional to it —
 * the natural consequence of a 1990 codebase computing a note-like integer and
 * handing it to a logarithmic frequency table.
 */
export function engineIndexForThrottle(throttle: number): number {
  if (!Number.isFinite(throttle)) return ENGINE_INDEX_MIN;
  const clamped = Math.min(1, Math.max(0, throttle));
  return ENGINE_INDEX_MIN + Math.floor(ENGINE_INDEX_SPAN * clamped);
}

/**
 * MEASURED: at a *fixed* minimum-throttle setting in a descent the index crept
 * back up **6 steps** (24.37 → 26.55 Hz) over 24 s as the aircraft accelerated
 * (`rb_012.dro`, t = 32-66 s) — the sim's modelled propeller windmilling. The
 * driving quantity is engine RPM; throttle is only its main input.
 *
 * INFERRED (scaling): the capture pins the *size* of the effect (6 steps) but
 * not its airspeed curve, because only one descent was flown. This ramps the
 * full 6 steps linearly across cruise→dive, reusing the airspeed band
 * `continuous-params.ts`'s wind bed already calibrates against
 * (`docs/game/flight-tunables.md` §8.1: cruise 35-48 m/s, a calibrated dive
 * reaches 100 m/s). Below the floor the propeller is not being driven by the
 * airstream at all; above the ceiling the term saturates.
 */
export const WINDMILL_AIRSPEED_FLOOR_MPS = 30;
export const WINDMILL_AIRSPEED_CEILING_MPS = 75;
/** MEASURED: the whole windmilling contribution the capture showed, in pitch-index steps. */
export const WINDMILL_MAX_STEPS = 6;

/** INFERRED scaling of the MEASURED 6-step windmilling term — see `WINDMILL_AIRSPEED_FLOOR_MPS`. */
export function windmillIndexSteps(airspeedMps: number): number {
  if (!Number.isFinite(airspeedMps)) return 0;
  const span = WINDMILL_AIRSPEED_CEILING_MPS - WINDMILL_AIRSPEED_FLOOR_MPS;
  const t = Math.min(1, Math.max(0, (airspeedMps - WINDMILL_AIRSPEED_FLOOR_MPS) / span));
  return WINDMILL_MAX_STEPS * t;
}

/**
 * The pitch index the RPM state is heading for: the throttle law plus the
 * windmilling term, clamped to the global range both captured aircraft shared.
 * Adding rather than maxing is what the capture shows (the index crept *up*
 * from a floor the throttle had already pinned), and the clamp is what keeps it
 * from inventing pitches above the measured ceiling at high throttle.
 */
export function engineTargetIndex(throttle: number, airspeedMps: number): number {
  const raw = engineIndexForThrottle(throttle) + Math.round(windmillIndexSteps(airspeedMps));
  return Math.min(ENGINE_INDEX_MAX, Math.max(ENGINE_INDEX_MIN, raw));
}

/** The `0xB0` block (octave) field for a pitch index. */
export function blockForIndex(index: number): number {
  return Math.floor(index / FNUM_TABLE_SIZE);
}

/** The 10-bit F-number for a pitch index. */
export function fnumForIndex(index: number): number {
  return SIERRA_FNUM_TABLE[index % FNUM_TABLE_SIZE] ?? 0;
}

/** The OPL2's native output rate, restated here so the frequency law reads as one expression. */
const OPL_CLOCK_RATE = 49_716;

/** `f = FNUM × 49716 / 2^(20 − BLOCK)` — the datasheet's own law, which `packages/baker`'s chip reproduces to within 1%. */
export function carrierHzForIndex(index: number): number {
  return (fnumForIndex(index) * OPL_CLOCK_RATE) / 2 ** (20 - blockForIndex(index));
}

/** One OPL2 register write, `[register, value]` — the unit the recipe is expressed in. */
export type RegisterWrite = readonly [number, number];

/** Register-address offset of OPL2 channel *n*'s modulator; the carrier sits three addresses higher. */
const OPERATOR_OFFSET: readonly number[] = [0x00, 0x01, 0x02, 0x08, 0x09, 0x0a, 0x10, 0x11, 0x12];

/**
 * One operator's nine patch fields, in the shape `@red-opus/formats`'
 * `decodeOplBank` produces them — so `engine-voice.test.ts` can assert these
 * constants against the real `003:` chunk field by field rather than trusting a
 * transcription.
 */
export interface EngineOperatorPatch {
  readonly keyScaleLevel: number;
  readonly frequencyMultiplier: number;
  readonly attackRate: number;
  readonly decayRate: number;
  readonly sustainLevel: number;
  readonly releaseRate: number;
  readonly totalLevel: number;
  readonly waveform: number;
  /** Every operator in this patch family is EG-sustaining; a percussive one would decay away under a held key. */
  readonly sustaining: boolean;
}

export interface EngineChannelPatch {
  readonly modulator: EngineOperatorPatch;
  readonly carrier: EngineOperatorPatch;
  /** 7 — the maximum. Maximum operator self-feedback is what turns this voice from a tone into a broadband growl. */
  readonly feedback: number;
}

/**
 * MEASURED: bank program 4, the patch the driver wrote for `SIMSNDS` cue 2 at
 * sim entry, matched on all nine patch registers of the live capture.
 *
 * `carrier.totalLevel` is **4**, not the bank's stored 0: that is what Sierra's
 * velocity/volume chain computes for cue 2's velocity 64 at master volume 15,
 * and the capture confirms register `0x43` is written once at patch load and
 * never again across ~1500 engine-related register writes.
 */
export const ENGINE_PROGRAM_4: EngineChannelPatch = {
  modulator: { keyScaleLevel: 0, frequencyMultiplier: 0, attackRate: 15, decayRate: 0, sustainLevel: 0, releaseRate: 0, totalLevel: 0, waveform: 1, sustaining: true },
  carrier: { keyScaleLevel: 0, frequencyMultiplier: 1, attackRate: 15, decayRate: 3, sustainLevel: 0, releaseRate: 9, totalLevel: 4, waveform: 3, sustaining: true },
  feedback: 7,
};

/**
 * MEASURED: bank program 5 — the patch family the sim starts for *other*
 * aircraft (`SIMSNDS` cues 3 and 28 send programs 5 and 33). Identical
 * envelopes, waveforms and feedback to program 4; only the total levels differ.
 * Its carrier total level is overwritten every step of the distance ladder, so
 * the value here is only the key-on default.
 */
export const ENEMY_PROGRAM_5: EngineChannelPatch = {
  modulator: { keyScaleLevel: 0, frequencyMultiplier: 0, attackRate: 15, decayRate: 0, sustainLevel: 0, releaseRate: 0, totalLevel: 12, waveform: 1, sustaining: true },
  carrier: { keyScaleLevel: 0, frequencyMultiplier: 1, attackRate: 15, decayRate: 3, sustainLevel: 0, releaseRate: 9, totalLevel: 7, waveform: 3, sustaining: true },
  feedback: 7,
};

function operatorWrites(offset: number, operator: EngineOperatorPatch): RegisterWrite[] {
  return [
    [0x20 + offset, (operator.sustaining ? 0x20 : 0) | operator.frequencyMultiplier],
    [0x40 + offset, (operator.keyScaleLevel << 6) | operator.totalLevel],
    [0x60 + offset, (operator.attackRate << 4) | operator.decayRate],
    [0x80 + offset, (operator.sustainLevel << 4) | operator.releaseRate],
    [0xe0 + offset, operator.waveform],
  ];
}

/**
 * The twelve register writes that load a patch onto one OPL2 channel — ten
 * operator writes plus `0xC0` (feedback + FM), in the same order Sierra's
 * driver emits them. `packages/baker/src/adlib-driver.ts` is deliberately *not*
 * reused: the engine voice needs exactly these writes plus one `0xA0` per pitch
 * step, and the MIDI driver would drag the whole bank decoder into the worklet.
 */
export function patchRegisterWrites(channel: number, patch: EngineChannelPatch): RegisterWrite[] {
  const offset = OPERATOR_OFFSET[channel] ?? 0;
  return [
    ...operatorWrites(offset, patch.modulator),
    ...operatorWrites(offset + 3, patch.carrier),
    [0xc0 + channel, patch.feedback << 1],
  ];
}

/** `0x40` (KSL + total level) for one channel's carrier — the only register the distance model rewrites. */
export function carrierLevelWrite(channel: number, totalLevel: number, keyScaleLevel: number): RegisterWrite {
  const offset = (OPERATOR_OFFSET[channel] ?? 0) + 3;
  return [0x40 + offset, (keyScaleLevel << 6) | (totalLevel & 0x3f)];
}

/**
 * MEASURED: the sixteen carrier total levels the sim steps other aircraft
 * through as they close, about every 330 ms, keying off entirely when the
 * aircraft leaves earshot. Index 0 is the faintest. That ladder *is* the
 * original's entire distance model — the player's own engine is
 * pitch-modulated at constant level, everyone else is level-modulated at
 * constant pitch (fixed index 28, 24.37 Hz).
 */
export const ENEMY_LEVEL_LADDER: readonly number[] = [63, 51, 47, 44, 38, 33, 29, 26, 25, 23, 22, 21, 20, 19, 18, 17];

/** MEASURED: the interval between distance-ladder steps in the capture. */
export const ENEMY_LADDER_STEP_S = 0.33;

/**
 * INFERRED: the capture gives the ladder and its cadence but never a range in
 * metres, so the range→rung mapping is a port-side choice. Logarithmic in range
 * (each rung roughly a fixed *ratio* closer) matches how the measured total
 * levels themselves are spaced — big jumps at the far end, one-unit steps up
 * close. Beyond `ENEMY_AUDIBLE_RANGE_M` the voice keys off, which is what the
 * capture shows happening "out of earshot".
 */
export const ENEMY_AUDIBLE_RANGE_M = 1500;
export const ENEMY_CLOSEST_RANGE_M = 30;

/** INFERRED range→ladder-rung mapping; `null` means "out of earshot, key off". */
export function enemyLevelIndexForRange(rangeM: number | null | undefined): number | null {
  if (rangeM === null || rangeM === undefined || !Number.isFinite(rangeM)) return null;
  if (rangeM > ENEMY_AUDIBLE_RANGE_M) return null;
  const clamped = Math.max(ENEMY_CLOSEST_RANGE_M, rangeM);
  const t = Math.log(ENEMY_AUDIBLE_RANGE_M / clamped) / Math.log(ENEMY_AUDIBLE_RANGE_M / ENEMY_CLOSEST_RANGE_M);
  return Math.min(ENEMY_LEVEL_LADDER.length - 1, Math.max(0, Math.round(t * (ENEMY_LEVEL_LADDER.length - 1))));
}

/** MEASURED: the DOS guest's BIOS tick, the grid every observed pitch update landed on. */
export const BIOS_TICK_HZ = 18.2065;
/** MEASURED: the fastest observed rate is one index step per two ticks — 109.9 ms, 9.1 steps/s. */
export const MIN_TICKS_PER_STEP = 2;

/**
 * First-order lag time constant on the engine-speed state, in seconds.
 *
 * **2.2 s, chosen to reproduce the measurements — not the 4 s the source
 * documents quote.** Both docs state "τ ≈ 4 s", but that figure was fitted
 * under a *linear-RPM* reading (`d(rpm)/dt = (target − rpm)/τ` with the index
 * as `log rpm`), while the same documents' implementation note prescribes the
 * *index-linear* formulation used here ("keep a first-order lag on an
 * engine-speed variable, map it to the index with the law above") — and the
 * cockpit tachometer reading in the capture agrees with index-linear: ~1350 rpm
 * at index 28 to ~2150 rpm at index 85, "moving roughly linearly with the
 * index". The two are not interchangeable, and τ = 4 s in this formulation
 * gives a 19.0 s full-range settle against a measured 10.4 s.
 *
 * Simulated against the three end-to-end numbers the capture actually measured
 * (see `engine-voice.test.ts`, which asserts all of them):
 *
 * | τ | full-range settle (measured 10.4 s) | one notch (measured 4-5 s) |
 * |---|---|---|
 * | 1.8 s | 8.6 s | 4.2 s |
 * | **2.2 s** | **10.4 s** | **5.1 s** |
 * | 3.0 s | 14.2 s | 6.9 s |
 * | 4.0 s | 19.0 s | 9.2 s |
 */
export const ENGINE_RPM_TAU_S = 2.2;

/**
 * The lagged engine-speed state. Kept as an immutable value advanced by
 * `advanceRpmLag` so the dynamics are testable without a chip, a clock or a
 * worklet — `engine-voice-dsp.ts` just threads it through.
 */
export interface RpmLagState {
  /** Continuous, lagged pitch index — the engine-speed variable itself. */
  readonly indexState: number;
  /** The integer index actually written to the chip, rate-limited to `MIN_TICKS_PER_STEP`. */
  readonly emittedIndex: number;
  /** Sub-tick time carried into the next call, so the tick grid is independent of block size. */
  readonly carryS: number;
  /** Ticks since `emittedIndex` last moved, capped so it can't overflow while parked. */
  readonly ticksSinceStep: number;
}

export function initialRpmLagState(index: number): RpmLagState {
  return { indexState: index, emittedIndex: index, carryS: 0, ticksSinceStep: MIN_TICKS_PER_STEP };
}

/**
 * Advances the RPM state by `dtS` toward `targetIndex` on the guest's tick
 * grid. Two behaviours from the capture, both load-bearing:
 *
 * 1. **The glide.** Every throttle change produces a glide, not a jump: a
 *    first-order lag whose emitted index therefore moves at a constant
 *    (rate-limited) rate far from the target and slows exponentially near it.
 * 2. **The staircase.** Updates are quantised to the 18.2065 Hz tick and never
 *    move more than one index step per two ticks. The staircase is audible and
 *    is part of the original's character; reproducing it costs one clamp.
 */
export function advanceRpmLag(state: RpmLagState, dtS: number, targetIndex: number): RpmLagState {
  const tickS = 1 / BIOS_TICK_HZ;
  let { indexState, emittedIndex, ticksSinceStep } = state;
  let remaining = state.carryS + Math.max(0, Math.min(1, dtS));
  const decay = 1 - Math.exp(-tickS / ENGINE_RPM_TAU_S);
  while (remaining >= tickS) {
    remaining -= tickS;
    indexState += (targetIndex - indexState) * decay;
    ticksSinceStep = Math.min(MIN_TICKS_PER_STEP, ticksSinceStep + 1);
    const wanted = Math.round(indexState);
    if (wanted !== emittedIndex && ticksSinceStep >= MIN_TICKS_PER_STEP) {
      emittedIndex += Math.sign(wanted - emittedIndex);
      ticksSinceStep = 0;
    }
  }
  return { indexState, emittedIndex, carryS: remaining, ticksSinceStep };
}

/**
 * `packages/baker`'s render gain (the ×4 every baked `sfx/*.ogg` is written
 * through) expressed as a float scale. Rendered at that gain the measured
 * throttle sweep peaks at −9 dBFS and averages −15 dBFS — the budget
 * `audio-plan.md` §6 tells this bus to be designed around.
 */
export const BAKER_RENDER_GAIN = 4;

/**
 * MEASURED trim. The card's output filter (`ENGINE_LOWPASS_HZ`) is not
 * level-neutral: it removes the raw render's strongest octave band (12-25 kHz)
 * but its passband phase response lifts the *peak* to −7.7 dBFS even as the RMS
 * falls. 0.84 holds the filtered peak at −9.1 dBFS across an exhaustive
 * up-and-down sweep of the whole ladder — inside the −9 dBFS budget with a
 * little margin for the transients a glide passes through. The resulting RMS is
 * ≈ −17 dBFS, i.e. 2 dB *under* the −15 dBFS reference, which is the filter's
 * doing and is the safe direction to miss in.
 */
export const ENGINE_FILTER_PEAK_TRIM = 0.84;

/** Chip sample → float sample. `Opl2Chip.generate()` returns the same signed scale the bake writes to 16-bit PCM. */
export const ENGINE_OUTPUT_SCALE = (BAKER_RENDER_GAIN / 32768) * ENGINE_FILTER_PEAK_TRIM;

/**
 * The AdLib card's analogue output stage, which a bare emulator render does not
 * have: "a gentle one-pole/biquad low-pass in the 8-10 kHz region gets much
 * closer to hardware and also solves the resampling problem" (`audio-plan.md`
 * §6). Running it at the chip's own 49716 Hz — before resampling to the
 * `AudioContext` rate — is what makes it double as the anti-alias filter the
 * broadband voice needs; 4th-order Butterworth at 9 kHz is 68 dB down by 24 kHz.
 */
export const ENGINE_LOWPASS_HZ = 9_000;
/** Butterworth Q values for a 4th-order lowpass built from two cascaded biquads. */
export const ENGINE_LOWPASS_QS: readonly number[] = [0.541_196, 1.306_563];

/** The `AudioWorkletProcessor` name `engine-worklet.ts` registers and `engine-worklet-node.ts` constructs. */
export const ENGINE_VOICE_PROCESSOR = "redopus-engine-voice";

/** Public URL the bundled worklet module is served from (`dev-server.ts`'s route, `build.ts`'s second entrypoint). */
export const ENGINE_VOICE_WORKLET_URL = "/audio/engine-worklet.js";

/**
 * Everything the audio thread can be told. Both fields are optional and
 * independent, so a frame that only moved the throttle posts one number.
 *
 * There is deliberately no "level", "gain" or "retrigger" command: the original
 * never changes the engine voice's level and never re-keys it, and the whole
 * point of running the real chip is not to reintroduce those by the back door.
 */
export interface EngineVoiceCommand {
  /** Where the RPM state should head, in pitch-index units (`ENGINE_INDEX_MIN`…`ENGINE_INDEX_MAX`). */
  readonly targetIndex?: number;
  /** Rung of `ENEMY_LEVEL_LADDER` for the nearest other aircraft, or `null` to key that voice off. */
  readonly enemyLevelIndex?: number | null;
}

function readIndexField(source: Record<string, unknown>, key: string, min: number, max: number): number | undefined {
  const value = source[key];
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Math.min(max, Math.max(min, Math.round(value)));
}

/**
 * Validates one `postMessage` payload. A worklet's `port.onmessage` receives
 * whatever the structured clone delivered, so the audio thread never trusts its
 * shape: anything unrecognised yields `null` and is dropped, and every accepted
 * field is clamped into range rather than allowed to write a nonsense F-number.
 */
export function sanitizeEngineVoiceCommand(raw: unknown): EngineVoiceCommand | null {
  if (typeof raw !== "object" || raw === null) return null;
  const source = raw as Record<string, unknown>;
  const targetIndex = readIndexField(source, "targetIndex", ENGINE_INDEX_MIN, ENGINE_INDEX_MAX);
  const hasEnemy = "enemyLevelIndex" in source;
  const enemyValue = readIndexField(source, "enemyLevelIndex", 0, ENEMY_LEVEL_LADDER.length - 1);
  const enemyLevelIndex = hasEnemy ? (enemyValue ?? null) : undefined;
  if (targetIndex === undefined && enemyLevelIndex === undefined) return null;
  return { ...(targetIndex === undefined ? {} : { targetIndex }), ...(enemyLevelIndex === undefined ? {} : { enemyLevelIndex }) };
}
